'use client';

import type { BlogArticleProps } from '@routeroyal/theme-core';
import { sanitizeHTML } from '@routeroyal/ui-core';
import Image from 'next/image';
import { AllysBlogCard } from './AllysBlogCard';
import { 
  ShareIcon, 
  CalendarIcon, 
  ClockIcon,
  UserIcon 
} from '@heroicons/react/24/outline';

/**
 * AllysBlogArticle - Allys-themed blog article
 * Medium-inspired layout with Allys navy/gold colors and Inter font
 */
export function AllysBlogArticle({
  article,
  relatedArticles = [],
  onShare,
  locale = 'en',
}: BlogArticleProps) {
  return (
    <article className="max-w-4xl mx-auto [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif]">
      {/* Article Header */}
      <header className="mb-8">
        {/* Category */}
        {article.category && (
          <div className="mb-4">
            <span className="inline-block px-3 py-1 text-sm font-semibold text-white bg-[#243462] rounded-full">
              {article.category}
            </span>
          </div>
        )}

        {/* Title */}
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight text-[#243462]">
          {article.title}
        </h1>

        {/* Excerpt */}
        {article.excerpt && (
          <p className="text-xl text-gray-600 mb-6 leading-relaxed">
            {article.excerpt}
          </p>
        )}

        {/* Meta Information */}
        <div className="flex flex-wrap items-center gap-4 text-gray-600 border-t border-b border-gray-200 py-4">
          {/* Author */}
          <div className="flex items-center gap-3">
            {article.author.avatar ? (
              <div className="relative w-12 h-12 rounded-full overflow-hidden ring-2 ring-[#BBA339]/20">
                <Image
                  src={article.author.avatar}
                  alt={article.author.name}
                  fill
                  className="object-cover object-top"
                />
              </div>
            ) : (
              <div className="w-12 h-12 rounded-full bg-[#243462]/10 flex items-center justify-center">
                <UserIcon className="w-6 h-6 text-[#243462]" />
              </div>
            )}
            <div>
              <div className="font-semibold text-[#243462]">{article.author.name}</div>
              {article.author.bio && (
                <div className="text-sm text-gray-500">{article.author.bio}</div>
              )}
            </div>
          </div>

          <span className="hidden sm:inline">•</span>

          {/* Date */}
          <div className="flex items-center gap-2">
            <CalendarIcon className="w-5 h-5 text-[#BBA339]" />
            <time dateTime={article.publishedAt}>
              {new Date(article.publishedAt).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </time>
          </div>

          {/* Read Time */}
          {article.readTime && (
            <>
              <span className="hidden sm:inline">•</span>
              <div className="flex items-center gap-2">
                <ClockIcon className="w-5 h-5 text-[#BBA339]" />
                <span>{article.readTime}</span>
              </div>
            </>
          )}

          {/* Share Button */}
          {onShare && (
            <>
              <span className="hidden sm:inline ml-auto">•</span>
              <button
                onClick={() => onShare('twitter')}
                className="
                  flex items-center gap-2 px-4 py-2 
                  text-sm font-medium 
                  text-white bg-[#243462]
                  rounded-lg 
                  hover:bg-[#BBA339] 
                  transition-colors 
                  ml-auto sm:ml-0
                "
              >
                <ShareIcon className="w-4 h-4" />
                Share
              </button>
            </>
          )}
        </div>
      </header>

      {/* Featured Image */}
      {article.featuredImage && (
        <div className="relative w-full h-[400px] md:h-[500px] lg:h-[600px] mb-12 rounded-xl overflow-hidden ring-1 ring-gray-200">
          <Image
            src={article.featuredImage}
            alt={article.title}
            fill
            className="object-cover"
            priority
          />
        </div>
      )}

      {/* Article Content */}
      <div 
        className="
          prose prose-lg max-w-none mb-12
          [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif]
          prose-headings:font-['Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif]
          prose-headings:text-[#243462]
          prose-headings:font-bold
          prose-h1:text-4xl prose-h1:mb-6 prose-h1:mt-0
          prose-h2:text-3xl prose-h2:mb-4 prose-h2:mt-10 prose-h2:pb-2 prose-h2:border-b prose-h2:border-gray-200
          prose-h3:text-2xl prose-h3:mb-3 prose-h3:mt-8
          prose-h4:text-xl prose-h4:mb-2 prose-h4:mt-6
          prose-p:font-['Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif]
          prose-p:text-gray-700
          prose-p:leading-relaxed
          prose-p:mb-6
          prose-a:text-[#BBA339]
          prose-a:font-medium
          prose-a:no-underline
          hover:prose-a:underline
          prose-strong:text-[#243462]
          prose-strong:font-bold
          prose-em:italic
          prose-blockquote:font-['Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif]
          prose-blockquote:border-l-[#BBA339]
          prose-blockquote:border-l-4
          prose-blockquote:bg-[#BBA339]/5
          prose-blockquote:py-3 prose-blockquote:px-6 prose-blockquote:my-6 prose-blockquote:rounded-r-lg
          prose-blockquote:text-gray-700
          prose-blockquote:not-italic
          prose-code:text-[#243462]
          prose-code:bg-[#BBA339]/10
          prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded
          prose-code:before:content-none prose-code:after:content-none
          prose-pre:bg-[#243462] prose-pre:text-gray-100 prose-pre:rounded-lg prose-pre:my-6
          prose-ul:list-disc prose-ul:pl-6 prose-ul:my-4
          prose-ol:list-decimal prose-ol:pl-6 prose-ol:my-4
          prose-li:font-['Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif]
          prose-li:text-gray-700 prose-li:my-1 prose-li:leading-relaxed prose-li:marker:text-[#BBA339]
          prose-img:rounded-xl prose-img:my-6
          prose-hr:border-gray-200 prose-hr:my-8
          prose-table:w-full prose-table:border-collapse prose-table:my-6
          prose-thead:border-b-2 prose-thead:border-[#243462]/20
          prose-th:bg-[#243462]/5 prose-th:p-3 prose-th:text-left prose-th:font-semibold prose-th:text-[#243462] prose-th:border prose-th:border-gray-200
          prose-td:p-3 prose-td:border prose-td:border-gray-200 prose-td:text-gray-700
        "
        dangerouslySetInnerHTML={{ __html: sanitizeHTML(article.content) }}
      />

      {/* Tags */}
      {article.tags && article.tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-12 pb-12 border-b border-gray-200">
          {article.tags.map((tag) => (
            <span
              key={tag}
              className="
                px-3 py-1 
                text-sm 
                text-[#243462] 
                bg-[#BBA339]/10 
                rounded-full 
                hover:bg-[#BBA339]/20 
                transition-colors
                font-medium
              "
            >
              #{tag}
            </span>
          ))}
        </div>
      )}

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <section className="mt-16 pt-12 border-t border-gray-200">
          <h2 className="text-3xl font-bold mb-8 text-[#243462] [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif]">
            Related Articles
          </h2>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {relatedArticles.map((relatedArticle) => (
              <AllysBlogCard
                key={relatedArticle.id}
                article={relatedArticle}
                variant="compact"
                showExcerpt={true}
                showCategory={true}
                showAuthor={false}
                locale={locale}
              />
            ))}
          </div>
        </section>
      )}
    </article>
  );
}

